<template>
  <ol
    :class="[placement, !show && 'scale-0 opacity-0']"
    class="w-80 flex flex-col transition-all origin-bottom-right sonner-group z-0"
    role="alert"
    aria-live="assertive"
    aria-atomic="true"
    :aria-expanded="show ? 'true' : 'false'"
    style="--offset-y:0;"
    @mouseenter="setIsHovering(true)"
    @mouseleave="setIsHovering(false)"
  >

    <TransitionGroup name="sonner-transition">

      <SonnerToast
        v-for="(toast, index) in visibleToasts"
        :key="toast.id"
        :is-focused="getFocus(toast.id)"
        :toast="toast"
        :style="`--toasts-before:${visibleToasts.length - index};--z-index:${visibleToasts.length - index};--toast-height:${getHeight(toast.id)};`"
        :data-is-most-recent="index === visibleToasts.length - 1 ? 'true' : 'false'"
        :get-channel="(createOnMissing = false) => getChannel(createOnMissing, toast.conversationUuid)"
        class="sonner-toast"
        @before-unmount="() => removeChannel(toast?.conversationUuid)"
        @update:height="(h) => updateHeight(toast.id, h)"
        @update:is-focused="(f) => updateFocus(toast.id, f)"
        @dismiss="() => dismiss(toast.id)"
      />

    </TransitionGroup>

  </ol>
</template>

<script setup>
import SonnerToast from "./SonnerToast.vue";
import {nextTick, reactive, ref, watch} from "vue";

const emits = defineEmits(["dismiss"]);
const props = defineProps({
  placement: {
    type: String,
    default: "fixed bottom-4 right-6"
  },
  show: {
    type: Boolean,
    default: false,
  },
  currentToasts: {
    type: Array,
    default: () => [],
  },
  maxVisibleToasts: {
    type: Number,
    default: 3,
  },
});


const DEFAULT_MAX_HEIGHT = "124px";
const HOVERING_FOCUS_GRACE_PERIOD_MS = 2000;
const ANIMATION_DURATION_WITH_SHORT_GRACE_MS = 350;

const heights = reactive({});
const focuses = reactive({});
const visibleToasts = ref([]);
const websocketChannelMap = ref({});
const isHovering = ref(false);

const getIfVisibleToastHasFocus = () => {
  return visibleToasts.value.some((toast) => getFocus(toast.id));
};

const updateVisibleToasts = () => {
  if (!isHovering.value && !getIfVisibleToastHasFocus()) {
    visibleToasts.value = [...props.currentToasts].slice(-props.maxVisibleToasts);
    return;
  }

  setTimeout(updateVisibleToasts, HOVERING_FOCUS_GRACE_PERIOD_MS);
};

const getChannel = (createOnMissing = false, conversationUuid = null) => {
  if (!conversationUuid) return null;

  if (!websocketChannelMap[conversationUuid]) {
    if (!createOnMissing) return null;

    websocketChannelMap[conversationUuid] = {
      value: window.Echo.join(`Conversation.${conversationUuid}`),
      usedByCount: 0,
    };
  }

  websocketChannelMap[conversationUuid].usedByCount++;
  return websocketChannelMap[conversationUuid].value;
};

const removeChannel = async (conversationUuid) => {
  if (!conversationUuid || !websocketChannelMap[conversationUuid]) return;

  websocketChannelMap[conversationUuid].usedByCount--;

  await nextTick();

  if (websocketChannelMap[conversationUuid].usedByCount <= 0) {
    window.Echo.leave(`Conversation.${conversationUuid}`);
    delete websocketChannelMap[conversationUuid];
  }
};

const updateHeight = async (id, height) => {
  await nextTick();

  setTimeout(() => {
    heights[id] = height;
  }, ANIMATION_DURATION_WITH_SHORT_GRACE_MS); // Very important, otherwise animations glitch
};

const updateFocus = (id, focus) => focuses[id] = focus;

const getHeight = (id) => heights[id] || DEFAULT_MAX_HEIGHT;
const getFocus = (id) => focuses[id] || false;

const setIsHovering = (val) => isHovering.value = val;

const dismiss = (id) => emits("dismiss", id);

watch(() => props.currentToasts, () => {
  updateVisibleToasts();

  Object.keys(heights).forEach((id) => {
    if (!props.currentToasts.some((t) => t.id === id)) {
      delete heights[id];
    }
  });

  Object.keys(focuses).forEach((id) => {
    if (!props.currentToasts.some((t) => t.id === id)) {
      delete focuses[id];
    }
  });

  visibleToasts.value = visibleToasts.value.filter((toast) => props.currentToasts.some((t) => t.id === toast.id));
}, {immediate: true, deep: true});
</script>

<style scoped>
.sonner-toast {
  transition: 0.3s all ease-in-out;
  transform: translateY(var(--offset-y));
  margin-bottom: 6px;
}

@media only screen and (min-width: 640px) {
  .sonner-toast:not([data-is-most-recent="true"]) {
    transition: 0.3s all ease-in-out;
    max-height: 100px;
    overflow: hidden;
    margin-bottom: -90px;
    transform: scale(calc(1 - max(0, var(--toasts-before) - 1) * 0.07)) translateY(var(--offset-y));
  }

  .sonner-group:hover .sonner-toast {
    margin-bottom: 6px; /* Adds spacing when hovered */
    max-height: var(--toast-height, 164px);
    opacity: 1;
    transform: scale(1) translateY(var(--offset-y)); /* Expand on hover */
  }
}

.sonner-transition-leave-active {
  transition: none !important;
  display: none !important;
  opacity: 0 !important;
}

.sonner-transition-enter-from {
  --offset-y: 200px;
  opacity: 0;
}

.sonner-transition-enter-to {
  --offset-y: 0;
  opacity: 1;
}

@media (prefers-reduced-motion: reduce) {
  .sonner-toast {
    transition: none !important;
  }
}
</style>
